const normalizePermission = (value) =>
  String(value || "")
    .trim()
    .toUpperCase();

export const permissionSetFromSession = (
  session
) => {
  const permissions =
    new Set();

  for (
    const value
    of session?.permissions || []
  ) {
    const permission =
      normalizePermission(value);

    if (permission) {
      permissions.add(permission);
    }
  }

  return permissions;
};

export const hasPermission = (
  permissions,
  permission
) => {
  if (permissions.has("ALL")) {
    return true;
  }

  return permissions.has(
    normalizePermission(permission)
  );
};

export const hasAllPermissions = (
  permissions,
  requiredPermissions
) => {
  if (
    !requiredPermissions
    || requiredPermissions.length === 0
  ) {
    return true;
  }

  if (permissions.has("ALL")) {
    return true;
  }

  return requiredPermissions.every(
    (permission) =>
      hasPermission(
        permissions,
        permission
      )
  );
};

export const hasAnyPermission = (
  permissions,
  requiredPermissions
) => {
  if (
    !requiredPermissions
    || requiredPermissions.length === 0
  ) {
    return true;
  }

  if (permissions.has("ALL")) {
    return true;
  }

  return requiredPermissions.some(
    (permission) =>
      hasPermission(
        permissions,
        permission
      )
  );
};

const isAllowed = (
  descriptor,
  permissions
) => {
  if (!descriptor) {
    return false;
  }

  const allRequirementsMet =
    hasAllPermissions(
      permissions,
      descriptor.requires
    );

  const anyRequirementMet =
    hasAnyPermission(
      permissions,
      descriptor.requiresAny
    );

  return (
    allRequirementsMet
    && anyRequirementMet
  );
};

const stripPermissionMetadata = (
  descriptor
) => {
  const {
    requires,
    requiresAny,
    options,
    ...visibleDescriptor
  } = descriptor;

  return visibleDescriptor;
};

const filterOptions = (
  options,
  permissions
) => {
  const visibleOptions = {};

  for (
    const [
      optionKey,
      option
    ]
    of Object.entries(
      options || {}
    )
  ) {
    if (
      !isAllowed(
        option,
        permissions
      )
    ) {
      continue;
    }

    visibleOptions[optionKey] =
      stripPermissionMetadata(
        option
      );
  }

  return visibleOptions;
};

const filterSection = (
  section,
  permissions
) => {
  if (
    !isAllowed(
      section,
      permissions
    )
  ) {
    return null;
  }

  const hasOptions =
    section.options
    && Object.keys(
      section.options
    ).length > 0;

  if (!hasOptions) {
    return stripPermissionMetadata(
      section
    );
  }

  const visibleOptions =
    filterOptions(
      section.options,
      permissions
    );

  const firstVisibleOption =
    Object.values(
      visibleOptions
    )[0];

  if (!firstVisibleOption) {
    return null;
  }

  return {
    ...stripPermissionMetadata(
      section
    ),

    path:
      firstVisibleOption.path
      || section.path,

    options:
      visibleOptions
  };
};

export const filterMenuByPermissions = (
  menuDefinition,
  session
) => {
  const permissions =
    permissionSetFromSession(
      session
    );

  const visibleMenu = {};

  for (
    const [
      sectionKey,
      section
    ]
    of Object.entries(
      menuDefinition || {}
    )
  ) {
    const visibleSection =
      filterSection(
        section,
        permissions
      );

    if (visibleSection) {
      visibleMenu[sectionKey] =
        visibleSection;
    }
  }

  return visibleMenu;
};